import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiError,
  claimCharacter,
  listCharacters,
  searchCharacters,
  setCharacterPin,
  verifyCharacterPin,
  type Character,
} from '../../lib/api';
import { IconAvatar } from './IconAvatar';
import { CharacterCreate } from './CharacterCreate';
import { PinPad } from './PinPad';

interface Props {
  onClaimed: (c: Character) => void;
}

const RECENT_KEY = 'tt:recentChars';
const RECENT_MAX = 6;

function loadRecentIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}

function pushRecent(charId: string): void {
  try {
    const cur = loadRecentIds().filter((id) => id !== charId);
    cur.unshift(charId);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, RECENT_MAX)));
  } catch { /* */ }
}

type PinFlow =
  | { kind: 'verify'; char: Character; busy: boolean; error: string | null; locked: boolean; lockedSec: number }
  | { kind: 'set'; char: Character; busy: boolean; error: string | null }
  | null;

export function CharacterSelect({ onClaimed }: Props) {
  const [chars, setChars] = useState<Character[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Character[] | null>(null);
  const [recents, setRecents] = useState<Character[]>([]);

  const [pinFlow, setPinFlow] = useState<PinFlow>(null);

  // Load full grid + hydrate recents (by charId) once.
  async function load() {
    setError(null);
    try {
      const res = await listCharacters();
      setChars(res.items);
      const ids = loadRecentIds();
      const byId = new Map(res.items.map((c) => [c.charId, c]));
      const found = ids.map((id) => byId.get(id)).filter((c): c is Character => !!c);
      setRecents(found);
    } catch {
      setError('Could not load characters.');
    }
  }
  useEffect(() => { void load(); }, []);

  // Debounced search.
  const searchSeq = useRef(0);
  useEffect(() => {
    const q = query.trim();
    if (!q) { setSearchResults(null); return; }
    const seq = ++searchSeq.current;
    const t = window.setTimeout(async () => {
      try {
        const res = await searchCharacters(q);
        if (seq === searchSeq.current) setSearchResults(res.items);
      } catch {
        if (seq === searchSeq.current) setSearchResults([]);
      }
    }, 180);
    return () => window.clearTimeout(t);
  }, [query]);

  function pick(c: Character) {
    setError(null);
    if (c.hasPin) {
      setPinFlow({ kind: 'verify', char: c, busy: false, error: null, locked: false, lockedSec: 0 });
      return;
    }
    // No PIN — try the legacy claim path. The server gates this on
    // owner-window + soft-owner; if rejected we fall into set-PIN flow.
    void (async () => {
      try {
        const claimed = await claimCharacter(c.charId);
        pushRecent(claimed.charId);
        onClaimed(claimed);
      } catch (e) {
        if (e instanceof ApiError && (e.code === 'pin-required-tofu' || e.code === 'not-soft-owner')) {
          setPinFlow({ kind: 'set', char: c, busy: false, error: null });
          return;
        }
        if (e instanceof ApiError && e.code === 'pin-required') {
          setPinFlow({ kind: 'verify', char: c, busy: false, error: null, locked: false, lockedSec: 0 });
          return;
        }
        setError('Could not claim that one. Try again.');
      }
    })();
  }

  async function submitVerify(pin: string) {
    if (!pinFlow || pinFlow.kind !== 'verify') return;
    const char = pinFlow.char;
    setPinFlow({ ...pinFlow, busy: true, error: null });
    try {
      const c = await verifyCharacterPin(char.charId, pin);
      pushRecent(c.charId);
      setPinFlow(null);
      onClaimed(c);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'locked') {
          // Server returns `{ error: 'locked', retryAfterSec: number }`.
          // Pull the seconds out so PinPad can run a real countdown.
          const ra = (e.body as { retryAfterSec?: unknown } | null)?.retryAfterSec;
          const lockedSec = typeof ra === 'number' && Number.isFinite(ra) && ra > 0 ? Math.floor(ra) : 0;
          setPinFlow({ kind: 'verify', char, busy: false, error: null, locked: true, lockedSec });
          return;
        }
        if (e.code === 'invalid-pin') {
          setPinFlow({ kind: 'verify', char, busy: false, error: 'Wrong PIN. Try again.', locked: false, lockedSec: 0 });
          return;
        }
        if (e.code === 'pin-not-set') {
          // Edge race: PIN was cleared between list and verify. Fall back
          // to set-flow so the user isn't stuck.
          setPinFlow({ kind: 'set', char, busy: false, error: null });
          return;
        }
      }
      setPinFlow({ kind: 'verify', char, busy: false, error: 'Could not verify. Try again.', locked: false, lockedSec: 0 });
    }
  }

  async function submitSet(pin: string) {
    if (!pinFlow || pinFlow.kind !== 'set') return;
    const char = pinFlow.char;
    setPinFlow({ ...pinFlow, busy: true, error: null });
    try {
      await setCharacterPin(char.charId, pin);
      // /pin/set already mints the actor cookie + writes session, so we
      // can hand the same character object back to the parent. Refresh
      // hasPin so future clicks know this is now PIN-protected.
      const claimed: Character = { ...char, hasPin: true };
      pushRecent(claimed.charId);
      setPinFlow(null);
      onClaimed(claimed);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'pin-already-set') {
        // Someone else won the TOFU race. Fall over to verify so the
        // legitimate owner can still try.
        setPinFlow({ kind: 'verify', char, busy: false, error: 'Someone already set a PIN. Enter it to log in.', locked: false, lockedSec: 0 });
        return;
      }
      // Surface specific reasons so a failed set is diagnosable instead
      // of all collapsing to "could not set". Migration-window failures
      // (`not-soft-owner`) used to swallow into the generic message and
      // strand legit owners when ops forgot to close the SSM deadline.
      let msg = 'Could not set PIN. Try again.';
      if (e instanceof ApiError) {
        if (e.code === 'not-soft-owner') {
          msg = 'Could not set PIN. Try again.';
        } else if (e.code === 'pin-set-rate-limited') {
          msg = 'Too many PIN attempts on this device. Try again in an hour.';
        } else if (e.code === 'bad-pin-format') {
          msg = 'PIN must be 4 digits.';
        } else if (e.code) {
          msg = `Could not set PIN (${e.code}). Try again.`;
        }
      }
      setPinFlow({ kind: 'set', char, busy: false, error: msg });
    }
  }

  // Decide what to render in the main grid: search results when querying,
  // otherwise the full list. MUST be defined before any early return so
  // hook order stays stable across renders (React error #300).
  const mainList = useMemo<Character[] | null>(() => {
    if (query.trim()) return searchResults ?? [];
    return chars;
  }, [query, searchResults, chars]);

  if (creating) {
    return (
      <CharacterCreate
        onCancel={() => setCreating(false)}
        onCreated={(c) => {
          setCreating(false);
          // New character has no PIN yet; the creator is the soft owner
          // so claim will succeed inside the owner window. After claim,
          // the parent app will (later) prompt them to set one.
          void pick(c);
        }}
      />
    );
  }

  return (
    <main className="select-screen">
      <div className="select-sconce select-sconce-l" aria-hidden="true">
        <span className="select-sconce-bracket" />
        <span className="select-sconce-candle" />
        <span className="select-sconce-flame" />
        <span className="select-sconce-flame-core" />
        <span className="select-sconce-halo" />
      </div>
      <div className="select-sconce select-sconce-r" aria-hidden="true">
        <span className="select-sconce-bracket" />
        <span className="select-sconce-candle" />
        <span className="select-sconce-flame" />
        <span className="select-sconce-flame-core" />
        <span className="select-sconce-halo" />
      </div>

      <header className="select-header select-banner-wrap">
        <span className="select-banner-cord select-banner-cord-l" aria-hidden="true" />
        <span className="select-banner-cord select-banner-cord-r" aria-hidden="true" />
        <div className="select-banner">
          <h1 className="display select-title">Who are you tonight?</h1>
          <span className="select-banner-tassel select-banner-tassel-l" aria-hidden="true" />
          <span className="select-banner-tassel select-banner-tassel-r" aria-hidden="true" />
        </div>
        <p className="muted">Pick a portrait — or build a new one.</p>
      </header>

      <div className="select-cta">
        <button
          type="button"
          className="select-create-plate"
          onClick={() => setCreating(true)}
        >
          <span className="select-create-frame" aria-hidden="true">+</span>
          <span className="select-create-label">Create a new character</span>
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 16px 14px' }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name…"
          aria-label="Search characters"
          style={{
            width: '100%', maxWidth: 420,
            padding: '10px 14px',
            borderRadius: 999,
            border: '1px solid rgba(255,200,120,0.22)',
            background: 'rgba(0,0,0,0.35)',
            color: 'var(--ink, #f0e6d2)',
            fontSize: 14,
          }}
        />
      </div>

      {error && <div role="alert" className="gate-rejection" style={{ textAlign: 'center' }}>{error}</div>}

      {!query && recents.length > 0 && (
        <section style={{ padding: '0 12px 10px' }}>
          <h2 className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 6px 4px' }}>
            On this device
          </h2>
          <div className="char-grid">
            {recents.map((c) => (
              <button
                key={`recent-${c.charId}`}
                type="button"
                className="char-tile"
                onClick={() => pick(c)}
              >
                <IconAvatar icon={c.icon} size={88} alt={c.name} />
                <div className="char-plate">
                  <span className="char-plate-name">{c.name}</span>
                  <span className="char-plate-points">{c.totalPoints} pts{c.hasPin ? ' • 🔒' : ''}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="char-grid">
        {!query && (
          <button
            type="button"
            className="char-tile new emphasised"
            onClick={() => setCreating(true)}
            aria-label="Create a new character"
          >
            <div className="char-plus" aria-hidden="true">+</div>
            <div className="char-name">New character</div>
            <div className="char-sub muted">build from scratch</div>
          </button>
        )}
        {mainList === null && Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="char-tile skeleton" />
        ))}
        {mainList?.length === 0 && query && (
          <div className="muted" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 24 }}>
            No matches.
          </div>
        )}
        {mainList?.map((c) => (
          <button
            key={c.charId}
            type="button"
            className="char-tile"
            onClick={() => pick(c)}
          >
            <IconAvatar icon={c.icon} size={88} alt={c.name} />
            <div className="char-plate">
              <span className="char-plate-name">{c.name}</span>
              <span className="char-plate-points">{c.totalPoints} pts{c.hasPin ? ' • 🔒' : ''}</span>
            </div>
          </button>
        ))}
      </div>

      {pinFlow?.kind === 'verify' && (
        <PinPad
          mode="verify"
          title="Enter PIN"
          subtitle={pinFlow.char.name}
          error={pinFlow.error ?? null}
          locked={pinFlow.locked}
          {...(pinFlow.lockedSec ? { lockedSecondsRemaining: pinFlow.lockedSec } : {})}
          onLockoutEnd={() => {
            // Countdown elapsed — flip locked off so the user can try
            // again. We keep the same char on screen so they don't lose
            // context.
            setPinFlow((cur) => (cur && cur.kind === 'verify'
              ? { ...cur, locked: false, lockedSec: 0 }
              : cur));
          }}
          busy={pinFlow.busy}
          onCancel={() => setPinFlow(null)}
          onSubmit={(pin) => void submitVerify(pin)}
        />
      )}
      {pinFlow?.kind === 'set' && (
        <PinPad
          mode="set"
          title="Set a 4-digit PIN"
          subtitle={`${pinFlow.char.name} — locks this character to your devices`}
          error={pinFlow.error ?? null}
          busy={pinFlow.busy}
          onCancel={() => setPinFlow(null)}
          onSubmit={(pin) => void submitSet(pin)}
        />
      )}
    </main>
  );
}
